
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export function useOptimisticPayment() {
  const [optimisticPurchases, setOptimisticPurchases] = useState<Set<number>>(new Set());
  const [isProcessing, setIsProcessing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  useEffect(() => {
    const handlePaymentSuccess = async () => {
      const searchParams = new URLSearchParams(location.search);
      const success = searchParams.get('success');
      const sessionId = searchParams.get('session_id');
      const bundleId = searchParams.get('bundle_id');
      const isEarlyAccess = searchParams.get('early_access') === 'true';

      if (success === 'true' && sessionId && bundleId) {
        const bundleIdNum = parseInt(bundleId);
        
        // Immediate optimistic update
        setOptimisticPurchases(prev => new Set(prev).add(bundleIdNum));
        setIsProcessing(true);
        
        // Show success toast immediately
        toast({
          title: isEarlyAccess ? "Pre-order Complete!" : "Bundle Unlocked!",
          description: isEarlyAccess 
            ? "Choose your early access book below" 
            : "You now have access to all books in this bundle",
        });

        // Clear URL parameters immediately
        const url = new URL(window.location.href);
        url.search = '';
        navigate(url.pathname, { replace: true });

        // Update specific queries optimistically
        queryClient.setQueryData(['bundlePurchase', bundleIdNum], true);
        
        // Background verification with fallback
        try {
          const { data, error } = await supabase.functions.invoke('verify-payment', {
            body: { sessionId }
          });

          if (error || data.status !== 'success') {
            console.error("Primary verification failed, trying fallback:", error);
            
            // Try automatic processing fallback
            setTimeout(async () => {
              try {
                await supabase.functions.invoke('process-pending-payments');
                // Refresh data after fallback processing
                queryClient.invalidateQueries({ queryKey: ['bundlePurchase', bundleIdNum] });
                queryClient.invalidateQueries({ queryKey: ['permanentBundleAccess'] });
              } catch (fallbackError) {
                console.error("Fallback processing failed:", fallbackError);
              }
            }, 3000); // Wait 3 seconds then try fallback
            
          } else {
            // Verification successful - refresh only the specific bundle data
            queryClient.invalidateQueries({ queryKey: ['bundlePurchase', bundleIdNum] });
            queryClient.invalidateQueries({ queryKey: ['permanentBundleAccess'] });
          }
        } catch (error) {
          console.error("Payment verification error:", error);
          
          // Try fallback processing after 5 seconds
          setTimeout(async () => {
            try {
              await supabase.functions.invoke('process-pending-payments');
              queryClient.invalidateQueries({ queryKey: ['bundlePurchase', bundleIdNum] });
              queryClient.invalidateQueries({ queryKey: ['permanentBundleAccess'] });
            } catch (fallbackError) {
              console.error("Fallback processing failed:", fallbackError);
            }
          }, 5000);
        } finally {
          setIsProcessing(false);
        }
      }
    };

    handlePaymentSuccess();
  }, [location.search, navigate, queryClient, toast]);

  const hasOptimisticAccess = (bundleId: number) => {
    return optimisticPurchases.has(bundleId);
  };

  return {
    hasOptimisticAccess,
    isProcessing
  };
}
